// PointClusterTileLayer.ts
import { UpdateParameters } from '@deck.gl/core';
import { TileLayer } from '@deck.gl/geo-layers';
import { Tile2DHeader, Tileset2D } from '@deck.gl/geo-layers/dist/tileset-2d';
import { GeoJsonLayer } from '@deck.gl/layers';
import { QueryClient } from '@tanstack/react-query';
import { isNumber, isString } from 'lodash';
import {
  CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1,
  CslCmpDeviceLocationsApiContractsGeoJsonFeatureV1,
} from 'src/clients/locations/Device Locations API/types.gen';

type TileFeatures = CslCmpDeviceLocationsApiContractsGeoJsonFeatureCollectionV1;
type TileFeature = CslCmpDeviceLocationsApiContractsGeoJsonFeatureV1;
type TileFeatureProperties = CslCmpDeviceLocationsApiContractsGeoJsonFeatureV1['properties'];

export class PointClusterTileLayer extends TileLayer<
  TileFeatures,
  { account: string; productTypes: string[]; queryClient: QueryClient }
> {
  static readonly layerName = 'PointClusterTileLayer';

  declare state: {
    tileset: Tileset2D | null;
    isLoaded: boolean;
    frameNumber?: number;
    maxCount: Record<number, number>;
  };

  initializeState() {
    super.initializeState();
    this.state.maxCount = {};
  }

  updateState(updateParams: UpdateParameters<this>) {
    super.updateState(updateParams);

    this.state.tileset?.selectedTiles?.forEach((t) => {
      if (t.content) {
        t.content.features.forEach((f: TileFeature) => {
          if (
            f.properties?.cluster &&
            'pointCount' in f.properties &&
            isNumber(f.properties.pointCount)
          ) {
            const z = Math.floor(this.context.viewport.zoom);
            this.state.maxCount[z] = Math.max(this.state.maxCount[z] ?? 0, f.properties.pointCount);
          }
        });
      }
    });
  }

  renderSubLayers(
    props: TileLayer['props'] & {
      id: string;
      data: TileFeatures;
      _offset: number;
      tile: Tile2DHeader<TileFeatures>;
    },
  ) {
    const { data, tile } = props;
    const { x, y, z } = tile.index;

    if (!data?.features) {
      return null;
    }

    const pointFeatures = data.features.filter(
      (f): f is GeoJSON.Feature<GeoJSON.Point> =>
        f.type === 'Feature' && f.geometry?.type === 'Point',
    );

    return new GeoJsonLayer<TileFeatureProperties>({
      id: `geojson-${x}-${y}-${z}`,
      data: pointFeatures,
      pickable: true,
      pointType: 'circle+text',
      pointRadiusUnits: 'pixels',
      getPointRadius: (f) => {
        if (f.properties?.cluster && isNumber(f.properties.pointCount)) {
          const z = Math.floor(this.context.viewport.zoom);
          return 12 + 16 * Math.sqrt(f.properties.pointCount / this.state.maxCount[z]);
        }

        return 9;
      },
      getFillColor: (f) => {
        if (f.properties?.cluster) {
          return [102, 124, 129]; //grey.600
        }
        if (
          isString(f.properties?.productType) &&
          f.properties?.productType.toLowerCase() === 'sim'
        ) {
          return [230, 126, 161];
        }

        if (
          isString(f.properties?.productType) &&
          f.properties?.productType.toLowerCase() === 'rsim'
        ) {
          return [97, 217, 200];
        }

        if (
          isString(f.properties?.productType) &&
          f.properties?.productType.toLowerCase() === 'esim'
        ) {
          return [126, 126, 230];
        }

        return [100, 100, 100];
      },
      getLineWidth: 1,
      lineWidthUnits: 'pixels',
      getLineColor: [255, 255, 255],
      getTextSize: 16,
      getTextColor: [255, 255, 255],
      getText: (f: TileFeature) => {
        if (f.properties?.cluster && f.properties?.pointCount) {
          if (isNumber(f.properties.pointCount)) {
            return f.properties.pointCount.toString();
          }

          if (isString(f.properties.pointCount)) {
            return f.properties.pointCount;
          }
        }
      },
    });
  }
}
